import React, { useState, useEffect, useCallback } from 'react';
import { Select, Spin, Tooltip } from 'antd';
import { deviceService } from '../services/deviceService';
import { DeviceBatch } from '../types';
import { useAuth } from '../contexts/AuthContext';

const { Option } = Select;

interface BatchSelectorProps {
  value?: number;
  onChange?: (value: number) => void;
  placeholder?: string;
  allowClear?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  dealerId?: number; // 经销商ID，用于筛选批次
}

/**
 * 批次选择器组件
 * 根据传入的经销商ID筛选批次列表
 * 如果没有传入经销商ID，则组件不可用（置灰）
 */
const BatchSelector: React.FC<BatchSelectorProps> = ({
  value,
  onChange,
  placeholder = '请选择批次',
  allowClear = true,
  style,
  disabled = false,
  dealerId,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState<DeviceBatch[]>([]);

  // 组件是否可用：传入的disabled为true或者没有dealerId时不可用
  const isDisabled = disabled || !dealerId;

  const loadBatches = useCallback(async () => {
    if (!dealerId || !user?.userId) {
      return;
    }

    try {
      setLoading(true);

      const response = await deviceService.getDeviceBatchList({
        adminUserId: user.userId,
        dealerId: dealerId,
        pageNo: 1,
        pageSize: 1000, // 获取所有批次
      });

      if (response.status === 1 && response.data) {
        setBatches(response.data.content || []);
      } else {
        console.error('加载批次列表失败:', response.msg);
        setBatches([]);
      }
    } catch (error) {
      console.error('加载批次列表失败:', error);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [dealerId, user?.userId]);

  // 加载批次列表
  useEffect(() => {
    if (dealerId && !disabled) {
      loadBatches();
    } else {
      // 如果没有dealerId，清空批次列表
      setBatches([]);
    }
  }, [dealerId, disabled, loadBatches]);

  const handleChange = (selectedValue: number) => {
    if (onChange) {
      onChange(selectedValue);
    }
  };

  return (
    <Select
      value={value}
      onChange={handleChange}
      placeholder={isDisabled ? '请先选择经销商' : placeholder}
      allowClear={allowClear}
      style={{ width: 250, ...style }}
      disabled={isDisabled}
      loading={loading}
      showSearch
      optionFilterProp="label"
      optionLabelProp="label"
      filterOption={(input, option) =>
        (option?.label as string)
          ?.toLowerCase()
          ?.includes(input.toLowerCase())
      }
      notFoundContent={loading ? <Spin size="small" /> : '暂无数据'}
    >
      {batches.map(batch => {
        const label = `${batch.batchName}${batch.deviceTypeName ? ` (${batch.deviceTypeName})` : ''}${batch.deviceCount ? ` - ${batch.deviceCount}台` : ''}`;
        return (
          <Option key={batch.id} value={batch.id} label={label}>
            <Tooltip title={label} placement="right" mouseEnterDelay={0.1}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label}
              </div>
            </Tooltip>
          </Option>
        );
      })}
    </Select>
  );
};

export default BatchSelector;
